import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  FaCamera,
  FaImage,
  FaTimes,
  FaCheck,
  FaRedo,
  FaUndo,
  FaMagic,
  FaFilePdf,
  FaFileImage,
  FaSlidersH,
} from "react-icons/fa";
import {
  detectDocumentCorners,
  getDefaultCorners,
  warpPerspective,
  applyScannerFilter,
  rotateCanvas,
  canvasToCompressedFile,
  formatFileSize,
} from "../../utils/imageScannerUtils";

export default function DocumentScannerModal({
  isOpen,
  onClose,
  onComplete,
  initialFile = null,
  title = "Document Scanner",
  targetFileName = "scanned_document.jpg",
}) {
  const [step, setStep] = useState("capture"); // "capture" | "crop" | "filter"
  const [imageSrc, setImageSrc] = useState(null);
  const [rawFile, setRawFile] = useState(null);

  // Crop state (coordinates in natural image pixels)
  const [corners, setCorners] = useState([]); // [{x,y}, {x,y}, {x,y}, {x,y}]
  const [activeCornerIdx, setActiveCornerIdx] = useState(null);
  const [loupePosition, setLoupePosition] = useState(null); // {x, y, clientX, clientY}

  // Filter state
  const [filterMode, setFilterMode] = useState("magic_color"); // "magic_color" | "bw" | "grayscale" | "original"
  const [flattenedCanvas, setFlattenedCanvas] = useState(null);
  const [previewDataUrl, setPreviewDataUrl] = useState(null);
  const [compressedFile, setCompressedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs
  const imageRef = useRef(null);
  const cropContainerRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Handle incoming initial file if provided
  useEffect(() => {
    if (isOpen && initialFile) {
      loadFile(initialFile);
    } else if (!isOpen) {
      resetState();
    }
  }, [isOpen, initialFile]);

  const resetState = () => {
    setStep("capture");
    setImageSrc(null);
    setRawFile(null);
    setCorners([]);
    setActiveCornerIdx(null);
    setLoupePosition(null);
    setFilterMode("magic_color");
    setFlattenedCanvas(null);
    setPreviewDataUrl(null);
    setCompressedFile(null);
    setIsProcessing(false);
  };

  const loadFile = (file) => {
    if (!file) return;
    setRawFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
      setStep("crop");
    };
    reader.readAsDataURL(file);
  };

  // When image loads in crop step, auto-detect corners
  const handleImageLoaded = () => {
    if (!imageRef.current) return;
    const detected = detectDocumentCorners(imageRef.current);
    setCorners(detected);
  };

  // Convert natural image coords to screen coords in crop container
  const getScreenCoords = useCallback((point) => {
    if (!imageRef.current || !point) return { x: 0, y: 0 };
    const img = imageRef.current;
    const rect = img.getBoundingClientRect();
    const scaleX = rect.width / (img.naturalWidth || 1);
    const scaleY = rect.height / (img.naturalHeight || 1);
    return {
      x: point.x * scaleX,
      y: point.y * scaleY,
    };
  }, []);

  // Convert screen touch/mouse coords to natural image coords
  const getImageCoords = useCallback((clientX, clientY) => {
    if (!imageRef.current) return { x: 0, y: 0 };
    const img = imageRef.current;
    const rect = img.getBoundingClientRect();
    const scaleX = (img.naturalWidth || 1) / rect.width;
    const scaleY = (img.naturalHeight || 1) / rect.height;

    const boundedX = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const boundedY = Math.max(0, Math.min(rect.height, clientY - rect.top));

    return {
      x: Math.round(boundedX * scaleX),
      y: Math.round(boundedY * scaleY),
    };
  }, []);

  // Pointer/Touch Drag handlers for 4 corners
  const handlePointerDown = (idx, e) => {
    e.preventDefault();
    setActiveCornerIdx(idx);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setLoupePosition({ clientX, clientY, ...corners[idx] });
  };

  const handlePointerMove = useCallback((e) => {
    if (activeCornerIdx === null || !imageRef.current) return;
    e.preventDefault();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const newCoord = getImageCoords(clientX, clientY);

    setCorners((prev) => {
      const copy = [...prev];
      copy[activeCornerIdx] = newCoord;
      return copy;
    });

    setLoupePosition({ clientX, clientY, ...newCoord });
  }, [activeCornerIdx, getImageCoords]);

  const handlePointerUp = useCallback(() => {
    setActiveCornerIdx(null);
    setLoupePosition(null);
  }, []);

  useEffect(() => {
    if (activeCornerIdx !== null) {
      window.addEventListener("pointermove", handlePointerMove, { passive: false });
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("touchmove", handlePointerMove, { passive: false });
      window.addEventListener("touchend", handlePointerUp);
      return () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("touchmove", handlePointerMove);
        window.removeEventListener("touchend", handlePointerUp);
      };
    }
  }, [activeCornerIdx, handlePointerMove, handlePointerUp]);

  // Rotate image by 90 degrees in crop mode
  const handleRotateImage = () => {
    if (!imageRef.current) return;
    setIsProcessing(true);
    const canvas = document.createElement("canvas");
    canvas.width = imageRef.current.naturalWidth;
    canvas.height = imageRef.current.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imageRef.current, 0, 0);

    const rotated = rotateCanvas(canvas, 90);
    const newSrc = rotated.toDataURL("image/jpeg", 0.95);
    setImageSrc(newSrc);
    setIsProcessing(false);
  };

  // Reset corners to safe default
  const handleResetCorners = () => {
    if (!imageRef.current) return;
    const def = getDefaultCorners(imageRef.current.naturalWidth, imageRef.current.naturalHeight);
    setCorners(def);
  };

  // Run perspective deskew and transition to Filter Step
  const handleProcessWarp = () => {
    if (!imageRef.current || corners.length !== 4) return;
    setIsProcessing(true);
    setTimeout(() => {
      try {
        const warped = warpPerspective(imageRef.current, corners, 1800);
        setFlattenedCanvas(warped);
        updateFilteredPreview(warped, filterMode);
        setStep("filter");
      } catch (err) {
        console.error("Warp failed:", err);
      } finally {
        setIsProcessing(false);
      }
    }, 50);
  };

  // Apply chosen filter and update preview & compressed file
  const updateFilteredPreview = async (canvas, pattern) => {
    if (!canvas) return;
    setIsProcessing(true);
    try {
      const filtered = applyScannerFilter(canvas, pattern);
      const dataUrl = filtered.toDataURL("image/jpeg", 0.85);
      setPreviewDataUrl(dataUrl);

      // Generate compressed File object
      const name = targetFileName || rawFile?.name || "scanned_doc.jpg";
      const file = await canvasToCompressedFile(filtered, name, 0.82);
      setCompressedFile(file);
    } catch (err) {
      console.error("Filter update error:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFilterChange = (pattern) => {
    setFilterMode(pattern);
    if (flattenedCanvas) {
      updateFilteredPreview(flattenedCanvas, pattern);
    }
  };

  // Finish and return compressed file
  const handleConfirmScan = () => {
    if (!compressedFile) return;
    onComplete(compressedFile);
    onClose();
  };

  if (!isOpen) return null;

  // Compute SVG polygon points for crop overlay
  const screenCorners = corners.map(getScreenCoords);
  const polygonPointsStr = screenCorners.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-2 sm:p-4 overflow-y-auto">
      <div className="relative w-full max-w-2xl bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* MODAL HEADER */}
        <div className="px-4 py-3 bg-slate-900/90 border-b border-slate-800 flex items-center justify-between z-10 shrink-0">
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 text-sm">
              <FaCamera />
            </span>
            <div>
              <h3 className="text-sm font-bold text-white leading-tight">{title}</h3>
              <p className="text-[11px] text-slate-400">
                {step === "capture" && "Take a photo or choose an existing document image"}
                {step === "crop" && "Adjust the 4 corners to fit document borders"}
                {step === "filter" && "Choose scan enhancement pattern & compress"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition"
          >
            <FaTimes size={16} />
          </button>
        </div>

        {/* MODAL BODY */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col items-center justify-center min-h-[300px] bg-slate-950/50">
          {/* STEP 1: CAPTURE / SOURCE SELECTION */}
          {step === "capture" && (
            <div className="w-full max-w-md py-6 space-y-4 text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-tr from-emerald-600 to-cyan-500 flex items-center justify-center text-white text-2xl shadow-lg shadow-emerald-500/20">
                <FaCamera />
              </div>
              <div>
                <h4 className="text-base font-bold text-white">Capture or Upload Document</h4>
                <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
                  Automatically flattens perspective, sharpens text, and compresses document size for instant upload.
                </p>
              </div>

              {/* Hidden Inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && loadFile(e.target.files[0])}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && loadFile(e.target.files[0])}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="p-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex flex-col items-center justify-center gap-2 transition shadow-lg shadow-emerald-600/20 cursor-pointer"
                >
                  <FaCamera size={22} />
                  <span>Use Camera</span>
                  <span className="text-[10px] opacity-80 font-normal">Direct mobile snap</span>
                </button>
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  className="p-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex flex-col items-center justify-center gap-2 border border-slate-700 transition cursor-pointer"
                >
                  <FaImage size={22} className="text-cyan-400" />
                  <span>Choose from Files</span>
                  <span className="text-[10px] opacity-80 font-normal">Gallery or PDF photo</span>
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: 4-CORNER CROP & PERSPECTIVE ADJUSTMENT */}
          {step === "crop" && imageSrc && (
            <div className="relative w-full flex flex-col items-center select-none">
              <div
                ref={cropContainerRef}
                className="relative inline-block border border-slate-800 rounded-lg overflow-hidden bg-slate-900 max-h-[58vh]"
              >
                <img
                  ref={imageRef}
                  src={imageSrc}
                  alt="Scan document"
                  onLoad={handleImageLoaded}
                  className="block max-h-[58vh] w-auto object-contain pointer-events-none"
                />

                {/* SVG Crop Overlay */}
                {corners.length === 4 && (
                  <svg
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    style={{ overflow: "visible" }}
                  >
                    {/* Dim outside background using mask */}
                    <defs>
                      <mask id="cropMask">
                        <rect width="100%" height="100%" fill="white" />
                        <polygon points={polygonPointsStr} fill="black" />
                      </mask>
                    </defs>
                    <rect
                      width="100%"
                      height="100%"
                      fill="rgba(0,0,0,0.55)"
                      mask="url(#cropMask)"
                    />

                    {/* Green connecting boundary lines */}
                    <polygon
                      points={polygonPointsStr}
                      fill="rgba(16, 185, 129, 0.12)"
                      stroke="#10b981"
                      strokeWidth="2.5"
                      strokeDasharray="4 2"
                    />
                  </svg>
                )}

                {/* 4 Interactive Touch Handles */}
                {screenCorners.map((pos, idx) => {
                  const labels = ["TL", "TR", "BR", "BL"];
                  return (
                    <div
                      key={idx}
                      onPointerDown={(e) => handlePointerDown(idx, e)}
                      onTouchStart={(e) => handlePointerDown(idx, e)}
                      style={{
                        position: "absolute",
                        left: `${pos.x}px`,
                        top: `${pos.y}px`,
                        transform: "translate(-50%, -50%)",
                        touchAction: "none",
                      }}
                      className="w-10 h-10 flex items-center justify-center cursor-grab active:cursor-grabbing z-20"
                    >
                      <div className="w-6 h-6 rounded-full bg-emerald-500 border-2 border-white shadow-md shadow-black/80 flex items-center justify-center ring-4 ring-emerald-500/30">
                        <span className="text-[8px] font-black text-slate-950 uppercase select-none">
                          {labels[idx]}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {/* Touch Magnifier Loupe (shows zoom above user finger while dragging) */}
                {loupePosition && (
                  <div
                    style={{
                      position: "fixed",
                      left: `${loupePosition.clientX}px`,
                      top: `${Math.max(10, loupePosition.clientY - 95)}px`,
                      transform: "translate(-50%, -50%)",
                      pointerEvents: "none",
                    }}
                    className="w-24 h-24 rounded-full border-2 border-white shadow-2xl overflow-hidden bg-slate-900 z-50 ring-4 ring-emerald-500/50"
                  >
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        backgroundImage: `url("${imageSrc}")`,
                        backgroundPosition: `${-loupePosition.x * 2 + 48}px ${-loupePosition.y * 2 + 48}px`,
                        backgroundSize: `${(imageRef.current?.naturalWidth || 1) * 2}px ${(imageRef.current?.naturalHeight || 1) * 2}px`,
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                    {/* Crosshair */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-4 h-0.5 bg-rose-500/90" />
                      <div className="h-4 w-0.5 bg-rose-500/90 absolute" />
                    </div>
                  </div>
                )}
              </div>

              {/* Crop Controls Bar */}
              <div className="flex flex-wrap items-center justify-center gap-2 mt-3 text-xs">
                <button
                  type="button"
                  onClick={handleRotateImage}
                  disabled={isProcessing}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 flex items-center gap-1.5 transition"
                >
                  <FaRedo className="text-cyan-400" /> Rotate 90°
                </button>
                <button
                  type="button"
                  onClick={handleImageLoaded}
                  disabled={isProcessing}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 flex items-center gap-1.5 transition"
                >
                  <FaMagic className="text-emerald-400" /> Auto Detect
                </button>
                <button
                  type="button"
                  onClick={handleResetCorners}
                  disabled={isProcessing}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 flex items-center gap-1.5 transition"
                >
                  <FaUndo className="text-amber-400" /> Reset
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: FILTER / PATTERN & COMPRESSION SELECTION */}
          {step === "filter" && (
            <div className="w-full flex flex-col items-center space-y-4">
              {/* Processed Flat Preview */}
              <div className="relative inline-block border border-slate-800 rounded-lg overflow-hidden bg-white shadow-xl max-h-[50vh]">
                {previewDataUrl ? (
                  <img
                    src={previewDataUrl}
                    alt="Deskewed scan"
                    className="max-h-[50vh] w-auto object-contain"
                  />
                ) : (
                  <div className="p-12 text-slate-400 text-xs">Processing document...</div>
                )}
              </div>

              {/* Scanner Patterns Selection */}
              <div className="w-full max-w-lg space-y-2">
                <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider text-center">
                  Select Scanner Pattern / Enhancement:
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { id: "magic_color", label: "Magic Color", icon: <FaMagic />, desc: "Crisp white & vivid ink" },
                    { id: "bw", label: "B&W Photocopy", icon: <FaFilePdf />, desc: "High contrast document" },
                    { id: "grayscale", label: "Grayscale", icon: <FaSlidersH />, desc: "Smooth gray tones" },
                    { id: "original", label: "Original", icon: <FaFileImage />, desc: "Natural photo colors" },
                  ].map((pat) => (
                    <button
                      key={pat.id}
                      type="button"
                      onClick={() => handleFilterChange(pat.id)}
                      className={`p-2.5 rounded-xl border text-left transition flex flex-col justify-between ${
                        filterMode === pat.id
                          ? "bg-emerald-500/15 border-emerald-500 text-white shadow-sm ring-1 ring-emerald-500"
                          : "bg-slate-800/80 border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-slate-600"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 text-xs font-bold">
                        <span className={filterMode === pat.id ? "text-emerald-400" : "text-slate-400"}>
                          {pat.icon}
                        </span>
                        {pat.label}
                      </div>
                      <span className="text-[9.5px] text-slate-400 mt-1">{pat.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Compression Badge */}
              {compressedFile && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] font-semibold">
                  <span>✓ Compressed Size:</span>
                  <strong className="text-white">{formatFileSize(compressedFile.size)}</strong>
                  <span className="text-[10px] text-emerald-400/80">
                    (Optimized for fast mobile submission)
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* MODAL FOOTER */}
        <div className="px-4 py-3 bg-slate-900/90 border-t border-slate-800 flex items-center justify-between shrink-0">
          <div>
            {step === "crop" && (
              <button
                type="button"
                onClick={() => setStep("capture")}
                className="px-3 py-1.5 rounded-xl text-xs text-slate-400 hover:text-white hover:bg-slate-800 transition"
              >
                ← Retake / Change Photo
              </button>
            )}
            {step === "filter" && (
              <button
                type="button"
                onClick={() => setStep("crop")}
                className="px-3 py-1.5 rounded-xl text-xs text-slate-400 hover:text-white hover:bg-slate-800 transition"
              >
                ← Back to Crop
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-300 hover:bg-slate-800 transition"
            >
              Cancel
            </button>

            {step === "crop" && (
              <button
                type="button"
                onClick={handleProcessWarp}
                disabled={isProcessing}
                className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 flex items-center gap-1.5 transition disabled:opacity-50"
              >
                <FaCheck /> Crop &amp; Flatten ➔
              </button>
            )}

            {step === "filter" && (
              <button
                type="button"
                onClick={handleConfirmScan}
                disabled={!compressedFile || isProcessing}
                className="px-6 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 flex items-center gap-1.5 transition disabled:opacity-50"
              >
                <FaCheck /> Apply &amp; Attach to Form
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
